import { DataStore, SortDirection } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setListing,
  setSearchText,
  setSelected,
} from "../store/ducks/groupSetting";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import { GroupSetting } from "../models";
import { CreateGroupSettingInput } from "../models/GQL_API";
import { GroupSettingQueryVariables } from "../models/API_Query_Variables";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError } = useApp();
  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );

  async function fetch(startIndex: number, limit: number) {
    try {
      const listing = await DataStore.query(
        GroupSetting as any,
        (model: any) => model.deleted("eq", "0"),
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s) => s.createdAt(SortDirection.DESCENDING),
        }
      );

      // dispatch(setListing(listing));

      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function fetchByName(params: GroupSettingQueryVariables) {
    const { startIndex, limit, groupID, isDefault } = params;

    try {
      const listing = await DataStore.query(
        GroupSetting as any,
        (model: any) => {
          model.deleted("eq", "0");

          if (groupID) model.groupID("eq", groupID);
          if (isDefault !== undefined) model.isDefault("eq", isDefault);

          return model;
        },
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s: any) => s.createdAt(SortDirection.DESCENDING),
        }
      );

      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function get(id: string) {
    try {
      const single: GroupSetting =
        listing.length === 0
          ? await DataStore.query(GroupSetting as any, id)
          : listing.find((model: any) => model.id === id);

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(data: any) {
    try {
      const createInput: CreateGroupSettingInput = {
        groupID: data.groupID,
        statusID: data.statusID,
        needsVerification: data.needsVerification,
        needsDeposit: data.needsDeposit,
        isDefault: data.isDefault,
        amount: data.amount ? parseInt(data.amount) : 0,
        deleted: "0",
        createdAt: new Date().toISOString(),
        createdByID: session.sub,
        createdByName: session.name,
      };

      if (data.days) createInput.days = data.days;
      if (data.timeSlots) createInput.timeSlots = data.timeSlots;

      await DataStore.save(new GroupSetting(createInput as any));

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(id: string, data: any) {
    try {
      const original = await get(id);

      await DataStore.save(
        GroupSetting.copyOf(original!, (updated) => {
          updated.statusID =
            data.statusID !== undefined ? data.statusID : original!.statusID;
          updated.needsVerification =
            data.needsVerification !== undefined
              ? data.needsVerification
              : original!.needsVerification;
          updated.needsDeposit =
            data.needsDeposit !== undefined
              ? data.needsDeposit
              : original!.needsDeposit;
          updated.isDefault = data.isDefault
            ? data.isDefault
            : original!.isDefault;
          updated.amount = data.amount
            ? parseInt(data.amount)
            : original!.amount;
          updated.days = data.days ? data.days : original!.days;
          updated.timeSlots = data.timeSlots
            ? data.timeSlots
            : original!.timeSlots;
        })
      );

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(id: string) {
    try {
      const original = await get(id);

      await DataStore.save(
        GroupSetting.copyOf(original!, (updated) => {
          updated.deleted = "1";
        })
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(ids: any) {
    for (let id of ids) {
      try {
        await trash(id);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(id: any) {
    try {
      await DataStore.delete(id);

      dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "groupID",
      numeric: false,
      disablePadding: false,
      label: "Group",
    },
    {
      id: "isDefault",
      numeric: false,
      disablePadding: false,
      label: "Setting Type",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["groupID", "isDefault"];

  const options: any[] = [];

  for (let option of listing) {
    options.push({ label: option.name, value: option.id });
  }

  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Model`] = GroupSetting as any;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByName`] = fetchByName;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: GroupSetting[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));

  return api;
};

export default useResource;
